<template>
  <div class="ed-income">
    <!-- ── KPIs Ventas ── -->
    <section class="ed-income__section">
      <header class="ed-income__h">
        <span class="ed-income__h-num">V1</span>
        <h3 class="ed-income__h-title">Resumen de ventas</h3>
        <span class="ed-income__h-rule"></span>
      </header>

      <div class="ed-income__kpis">
        <article class="ed-mini ed-mini--sage">
          <span class="ed-mini__label">Total del período</span>
          <span class="ed-mini__value">{{ fmt(totalVentas) }} <em>{{ currencyCode }}</em></span>
          <span class="ed-mini__sub">Ingresos brutos</span>
        </article>
        <article class="ed-mini ed-mini--brass">
          <span class="ed-mini__label">Ticket promedio</span>
          <span class="ed-mini__value">{{ fmt(ticketPromedio) }} <em>{{ currencyCode }}</em></span>
          <span class="ed-mini__sub">Por día activo</span>
        </article>
        <article class="ed-mini ed-mini--ink">
          <span class="ed-mini__label">Mejor día</span>
          <span class="ed-mini__value">{{ mejorDia.label || '—' }}</span>
          <span class="ed-mini__sub">{{ mejorDia.value ? fmt(mejorDia.value) + ' ' + currencyCode : 'Sin datos' }}</span>
        </article>
        <article class="ed-mini ed-mini--graph">
          <span class="ed-mini__label">Días con venta</span>
          <span class="ed-mini__value">{{ diasActivos }}</span>
          <span class="ed-mini__sub">en el rango</span>
        </article>
      </div>
    </section>

    <!-- ── Tendencia diaria ── -->
    <section class="ed-income__section">
      <header class="ed-income__h">
        <span class="ed-income__h-num">V2</span>
        <h3 class="ed-income__h-title">Tendencia diaria</h3>
        <span class="ed-income__h-rule"></span>
      </header>

      <div class="ed-card" v-if="!loading">
        <div class="ed-card__head">
          <div>
            <p class="ed-card__eyebrow">Ventas por día</p>
            <h4 class="ed-card__title">Curva histórica</h4>
          </div>
        </div>
        <LineChart v-if="ventas_diarias.series.length > 0" :title_header="''" :data="ventas_diarias" />
        <div v-else class="ed-empty">Sin datos para el rango seleccionado.</div>
      </div>
      <div v-else class="ed-card ed-skeleton" style="height: 360px;"></div>
    </section>

    <!-- ── Por hora + por mes ── -->
    <section class="ed-income__section" v-if="!loading">
      <header class="ed-income__h">
        <span class="ed-income__h-num">V3</span>
        <h3 class="ed-income__h-title">Patrones</h3>
        <span class="ed-income__h-rule"></span>
        <span class="ed-income__h-meta">Comportamiento por hora y mes</span>
      </header>

      <div class="ed-grid ed-grid--2">
        <div class="ed-card" v-if="ventas_horas.series.length > 0">
          <div class="ed-card__head">
            <p class="ed-card__eyebrow">Por hora</p>
            <h4 class="ed-card__title">Distribución horaria</h4>
          </div>
          <LineChart :title_header="''" :data="ventas_horas" />
        </div>
        <div class="ed-card" v-if="ventas_mensuales.series.length > 0">
          <div class="ed-card__head">
            <p class="ed-card__eyebrow">Por mes</p>
            <h4 class="ed-card__title">Histórico mensual</h4>
          </div>
          <BarChart :title_header="''" :data="ventas_mensuales" :colors="false" />
        </div>
      </div>
    </section>

    <!-- ── Distribución por ubicación ── -->
    <section class="ed-income__section" v-if="!loading && ventas_location.series.length > 1">
      <header class="ed-income__h">
        <span class="ed-income__h-num">V4</span>
        <h3 class="ed-income__h-title">Por ubicación</h3>
        <span class="ed-income__h-rule"></span>
      </header>

      <div class="ed-grid ed-grid--2">
        <div class="ed-card">
          <div class="ed-card__head">
            <p class="ed-card__eyebrow">Distribución</p>
            <h4 class="ed-card__title">Participación por ubicación</h4>
          </div>
          <PieChart :title_header="''" :data="ventas_location" />
        </div>
        <div class="ed-card ed-card--rank">
          <div class="ed-card__head">
            <p class="ed-card__eyebrow">Ranking</p>
            <h4 class="ed-card__title">Top ubicaciones</h4>
          </div>
          <ol class="ed-rank">
            <li v-for="(loc, idx) in topLocations" :key="loc.label" class="ed-rank__row">
              <span class="ed-rank__pos">{{ String(idx + 1).padStart(2, '0') }}</span>
              <span class="ed-rank__name">{{ loc.label }}</span>
              <span class="ed-rank__bar"><span :style="{ width: loc.pct + '%' }"></span></span>
              <span class="ed-rank__val">{{ fmt(loc.value) }}</span>
            </li>
          </ol>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import LineChart from "@/components/Charts/Line.vue";
import BarChart  from "@/components/Charts/Bar.vue";
import PieChart  from "@/components/Charts/Pie.vue";

export default {
  components: { LineChart, BarChart, PieChart },
  data: () => ({
    loading: false,
    ventas_diarias:    { series: [], label: [] },
    ventas_horas:      { series: [], label: [] },
    ventas_mensuales:  { series: [], label: [] },
    ventas_location:   { series: [], label: [] },
  }),
  props: {
    filters: {
      type: Object,
      default: () => ({ business: null, location: null, department_id: null }),
    },
  },

  computed: {
    currencyCode() {
      const bu = this.$store.state.auth.bussines_units?.find(b => b.value === this.filters.business);
      return bu?.currency_code || "";
    },
    totalVentas() {
      return this.ventas_diarias.series.reduce((a, b) => a + Number(b || 0), 0);
    },
    diasActivos() {
      return this.ventas_diarias.series.filter(v => Number(v) > 0).length;
    },
    ticketPromedio() {
      return this.diasActivos > 0 ? this.totalVentas / this.diasActivos : 0;
    },
    mejorDia() {
      let max = -1, idx = -1;
      this.ventas_diarias.series.forEach((v, i) => {
        if (Number(v) > max) { max = Number(v); idx = i; }
      });
      return idx >= 0 ? { label: this.ventas_diarias.label[idx], value: max } : { label: "", value: 0 };
    },
    topLocations() {
      const rows = this.ventas_location.label.map((l, i) => ({
        label: l, value: Number(this.ventas_location.series[i] || 0),
      })).sort((a, b) => b.value - a.value).slice(0, 6);
      const max = rows[0]?.value || 1;
      return rows.map(r => ({ ...r, pct: Math.max(2, (r.value / max) * 100) }));
    },
  },

  watch: {
    filters: {
      handler(v) { this.updateData(v); },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    fmt(n) {
      if (!isFinite(n)) return "—";
      return this.currencyFormat
        ? this.currencyFormat(n)
        : Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 });
    },

    fetchCached(endpoint, payload) {
      return this.$store.dispatch("dashboard/fetchCached", {
        endpoint, payload,
        fetcher: async () => {
          const r = await this.api_Post(endpoint, payload);
          return await r.json();
        },
      });
    },

    async updateData(filter) {
      if (!filter || !filter.business) return;
      this.loading = true;
      try {
        const base = {
          business: filter.business,
          location: filter.location,
          from_date: filter.from_date,
          to_date: filter.to_date,
        };
        const [days, hours, monthly, locations] = await Promise.all([
          this.fetchCached("income/dashboard_daily",    { ...base, type: 2 }),
          this.fetchCached("income/dashboard_hours",    { ...base, type: 2 }),
          this.fetchCached("income/dashboard_montly",   { business: filter.business, from_date: filter.from_date, to_date: filter.to_date, invoice: 2 }),
          this.fetchCached("income/dashboard_location", { ...base, invoice: 2 }),
        ]);
        const collect = (arr, k1, k2) => {
          const l = [], s = [];
          for (const i of (arr || [])) { l.push(i[k1]); s.push(parseFloat(i[k2])); }
          return { label: l, series: s };
        };
        this.ventas_diarias   = collect(days,      "day_year",          "total_sales");
        this.ventas_horas     = collect(hours,     "hour_format",       "total_sales");
        this.ventas_mensuales = collect(monthly,   "month_year",        "total_sales");
        this.ventas_location  = collect(locations, "description_short", "total_amount_sum");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$ink:          #2A3547;
$muted:        #5A6A85;
$muted-soft:   #8A98AB;
$rule:         #E5EAF2;
$bg:           #F7F9FC;
$card:         #FFFFFF;
$primary:      #00897B;
$primary-soft: #E0F2F1;
$info:         #2196F3;
$info-soft:    #E3F2FD;
$accent:       #5C6BC0;
$accent-soft:  #E8EAF6;
$warning:      #F57F17;
$warning-soft: #FFF3E0;
$error:        #E53935;
$success:      #2E7D32;
$shadow: rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 8px 18px -6px;

$font: 'Plus Jakarta Sans', sans-serif;

.ed-income { display: flex; flex-direction: column; gap: 36px; padding: 12px 0 24px; font-family: $font; color: $ink; }
.ed-income__section { display: flex; flex-direction: column; gap: 14px; }

.ed-income__h { display: flex; align-items: center; gap: 14px; }
.ed-income__h-num { font-family: $font; font-size: 11px; font-weight: 700; color: $primary; letter-spacing: .14em; }
.ed-income__h-title { font-family: $font; font-weight: 700; font-size: 22px; letter-spacing: -.01em; color: $ink; margin: 0; }
.ed-income__h-rule { flex: 1; height: 1px; background: $rule; }
.ed-income__h-meta { font-family: $font; font-size: 11px; font-weight: 600; color: $muted; letter-spacing: .14em; text-transform: uppercase; }

// KPIs Ventas
.ed-income__kpis {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  border: 1px solid $rule; background: $card;
  border-radius: 14px; overflow: hidden; box-shadow: $shadow;
}
.ed-mini {
  padding: 18px 22px; border-right: 1px solid $rule;
  display: flex; flex-direction: column; gap: 4px;
  position: relative;
}
.ed-mini:last-child { border-right: 0; }
.ed-mini::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; }
.ed-mini--sage::before  { background: $primary; }
.ed-mini--brass::before { background: $warning; }
.ed-mini--ink::before   { background: $accent; }
.ed-mini--graph::before { background: $info; }
.ed-mini__label { font-family: $font; font-size: 10px; font-weight: 600; letter-spacing: .22em; text-transform: uppercase; color: $muted; }
.ed-mini__value { font-family: $font; font-weight: 800; font-size: 24px; line-height: 1.1; letter-spacing: -.02em; color: $ink; }
.ed-mini__value em { font-family: $font; font-style: normal; font-size: 11px; color: $muted-soft; margin-left: 6px; }
.ed-mini__sub   { font-family: $font; font-size: 12px; color: $muted; }

// Cards
.ed-card { background: $card; border: 1px solid $rule; border-radius: 14px; box-shadow: $shadow; padding: 22px 22px 12px; position: relative; }
.ed-card__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 10px; }
.ed-card__eyebrow { font-family: $font; font-size: 10px; font-weight: 600; color: $muted; letter-spacing: .22em; text-transform: uppercase; margin: 0 0 4px; }
.ed-card__title { font-family: $font; font-weight: 700; font-size: 20px; letter-spacing: -.01em; color: $ink; margin: 0; }

.ed-grid { display: grid; gap: 18px; }
.ed-grid--2 { grid-template-columns: 1fr 1fr; }

// Rank list
.ed-card--rank { padding-bottom: 22px; }
.ed-rank { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 12px; }
.ed-rank__row {
  display: grid; grid-template-columns: 30px 1fr 90px auto; align-items: center; gap: 12px;
  padding-bottom: 12px; border-bottom: 1px dashed $rule;
}
.ed-rank__row:last-child { border-bottom: 0; }
.ed-rank__pos { font-family: $font; font-size: 11px; font-weight: 700; color: $primary; letter-spacing: .08em; }
.ed-rank__name { font-family: $font; font-weight: 600; font-size: 15px; color: $ink; letter-spacing: -.01em; }
.ed-rank__bar {
  height: 6px; background: #EEF1F6; border-radius: 999px; position: relative; overflow: hidden;
}
.ed-rank__bar span {
  position: absolute; left: 0; top: 0; bottom: 0;
  background: linear-gradient(90deg, $primary, lighten($primary, 18%));
  border-radius: 999px;
}
.ed-rank__val { font-family: $font; font-weight: 700; font-size: 13px; color: $ink; text-align: right; }

.ed-empty {
  padding: 60px 0; text-align: center;
  font-family: $font; color: $muted;
}

.ed-skeleton {
  background: linear-gradient(90deg, #F7F9FC 0%, #EEF1F6 50%, #F7F9FC 100%) !important;
  background-size: 200% 100% !important; animation: shimmer 1.4s linear infinite;
  border: 1px solid $rule; border-radius: 14px;
}
@keyframes shimmer { 0%{background-position:200% 0;} 100%{background-position:-200% 0;} }

@media (max-width: 880px) {
  .ed-grid--2 { grid-template-columns: 1fr; }
  .ed-income__kpis { grid-template-columns: 1fr 1fr; }
  .ed-mini:nth-child(2n) { border-right: 0; }
  .ed-mini { border-bottom: 1px solid $rule; }
}

::v-deep .modern-card {
  background: transparent !important; border: 0 !important;
  box-shadow: none !important; padding: 0 !important; border-radius: 0 !important;
}
</style>
